import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "V2.0/common/dialog";
import { Button } from "V2.0/common/button";
import InputComponent from "V2.0/components/InputComponent";
import { useCallback, useMemo, useState } from "react";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";
import { updateUserAccessLevelPermissions } from "V2.0/services/profile";
import { sortApplicationNavigationSections } from "V2.0/hooks/useRoleAccessBasedNavigation";
import {
  ApplicationSection,
  AssistantSection,
  UserAccessLevel,
} from "V2.0/models/UserProfile";
import AssistantSectionsVisibility from "./AssistantSectionsVisibility";
import ApplicationSectionsVisibility from "./ApplicationSectionsVisibility";

export default UsersModifyRoles;

function getFilteredAssistantVisibleSection(sections) {
  return sections.filter(
    (section) =>
      section != "communication" &&
      section != "changelog" &&
      section != "checkpoints" &&
      section != "assistantDashboard",
  );
}

function getFilteredApplicationVisibleSection(sections) {
  return sections.filter((section) => section != "dasboard");
}

function getModifyRoleInitialState(user) {
  return {
    userAccessLevel: user?.userAccessLevel ?? UserAccessLevel.WHITELABELUSER,
    applicationSectionsVisible:
      getFilteredApplicationVisibleSection(user?.applicationSectionsVisible) ??
      Object.values(ApplicationSection),
    assistantSectionsVisible:
      getFilteredAssistantVisibleSection(user?.assistantSectionsVisible) ??
      Object.values(AssistantSection),
  };
}

function useModifyRoleState(user) {
  const [state, setState] = useState(() => getModifyRoleInitialState(user));

  const updateUserAccessLevel = useCallback((userAccessLevel) => {
    setState((prev) => {
      return {
        ...prev,
        userAccessLevel,
      };
    });
  }, []);

  const unhideApplicationSection = useCallback((applicationSection) => {
    setState((prev) => {
      const updatedApplicationSections = [
        ...prev.applicationSectionsVisible,
        applicationSection,
      ];

      return {
        ...prev,
        applicationSectionsVisible: updatedApplicationSections,
      };
    });
  }, []);

  const hideApplicationSection = useCallback((applicationSection) => {
    setState((prev) => {
      const updatedApplicationSections = prev.applicationSectionsVisible.filter(
        (section) => section !== applicationSection,
      );

      return {
        ...prev,
        applicationSectionsVisible: updatedApplicationSections,
      };
    });
  }, []);

  const unhideAssistantSection = useCallback((assistantSection) => {
    setState((prev) => {
      const updatedAssistantSections = [
        ...prev.assistantSectionsVisible,
        assistantSection,
      ];

      return {
        ...prev,
        assistantSectionsVisible: updatedAssistantSections,
      };
    });
  }, []);

  const hideAssistantSection = useCallback((assistantSection) => {
    setState((prev) => {
      const updatedAssistantSections = prev.assistantSectionsVisible.filter(
        (section) => section !== assistantSection,
      );

      return {
        ...prev,
        assistantSectionsVisible: updatedAssistantSections,
      };
    });
  }, []);

  const value = useMemo(() => {
    return {
      state,
      unhideApplicationSection,
      hideApplicationSection,
      unhideAssistantSection,
      hideAssistantSection,
      updateUserAccessLevel,
    };
  }, [state]);

  return value;
}

function UsersModifyRoles(props) {
  const { onClose, fetchProfiles, user } = props;

  const {
    state,
    unhideApplicationSection,
    hideApplicationSection,
    unhideAssistantSection,
    hideAssistantSection,
    updateUserAccessLevel,
  } = useModifyRoleState(user);

  const [loading, setLoading] = useState<boolean>(false);

  const handleUpdate = useCallback(
    async (state) => {
      const payload = {
        ...state,
        applicationSectionsVisible: sortApplicationNavigationSections(
          state.applicationSectionsVisible,
        ),
      };

      const lifecycleCbs = {
        init() {
          setLoading(true);
          message.loading("Updating Role Permissions");
        },
        success() {
          setLoading(false);
          message.destroy();
          message.success("Role Permission Updated");
        },
        error(e) {
          setLoading(false);
          message.destroy();
          showErrorToast(e, "Failed to Update Role Permissions");
        },
      };

      lifecycleCbs.init();

      try {
        const response = await updateUserAccessLevelPermissions(
          user.user["_id"],
          payload,
        );

        if (response.status === 200) {
          lifecycleCbs.success();

          fetchProfiles();
          onClose();
        }
      } catch (e) {
        lifecycleCbs.error(e);
      }
    },
    [fetchProfiles, user],
  );

  const userAccessLevelSelect = (
    <InputComponent
      type="select"
      value={state.userAccessLevel}
      onChange={(e) => updateUserAccessLevel(e.target.value)}
      name="userAccessLevel"
      label="Role"
      options={[
        { id: 0, value: UserAccessLevel.WHITELABELUSER, label: "User" },
        { id: 1, value: UserAccessLevel.WHITELABELVIEWER, label: "Viewer" },
        { id: 2, value: UserAccessLevel.WHITELABELOPERATOR, label: "Operator" },
        // { id: 3, value: UserAccessLevel.WHITELABELOWNER, label: "Owner" },
      ]}
    />
  );

  return (
    <Dialog
      size="xl"
      open={true}
      onClose={onClose}
    >
      <DialogTitle>Modify Role</DialogTitle>
      <DialogBody className="space-y-8">
        {userAccessLevelSelect}
        <ApplicationSectionsVisibility
          disabled={
            state.userAccessLevel !== UserAccessLevel.WHITELABELOPERATOR
          }
          visibleSections={state.applicationSectionsVisible}
          onHideSection={hideApplicationSection}
          onUnhideSection={unhideApplicationSection}
        />
        <AssistantSectionsVisibility
          disabled={
            state.userAccessLevel !== UserAccessLevel.WHITELABELOPERATOR
          }
          visibleSections={state.assistantSectionsVisible}
          onHideSection={hideAssistantSection}
          onUnhideSection={unhideAssistantSection}
        />
      </DialogBody>
      <DialogActions>
        <Button
          outline
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          loading={loading}
          disabled={loading}
          onClick={() => handleUpdate(state)}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}
